import { useFormContext } from "react-hook-form";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";

import { PolicyUsage } from "@covertree/new-business-graphql";
import { countInterestsByType } from "@covertree/features";
import type {
  InsuredTabFormValues,
  InsuredTabAdditionalInterest,
} from "@redux/slices/endorsement";
import { useEndorsementOutletContext } from "@layouts/EndorsementLayout";

import type { AdditionalInterestFormData } from "./types";
import { toInterestFields } from "./utils";

const ACCORDION_EXPAND_DELAY_MS = 150;

/**
 * Add/edit/remove additional interests on the lifted Insured Info form — no
 * request; mutates the `additionalInterests` form array in place.
 */
export const useAdditionalInterest = () => {
  const { policy } = useEndorsementOutletContext();
  const isTenantPolicy = policy?.mainUnitType === PolicyUsage.Tenant;

  const { state } = useLocation();
  const [expandOnInit] = useState(Boolean(state?.expandAdditionalInterest));
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!expandOnInit) return;
    const timeoutId = window.setTimeout(() => {
      sectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, ACCORDION_EXPAND_DELAY_MS);
    return () => window.clearTimeout(timeoutId);
  }, [expandOnInit]);

  const { watch, getValues, setValue } = useFormContext<InsuredTabFormValues>();

  const additionalInterests = watch("additionalInterests") || [];

  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [removeId, setRemoveId] = useState<string | null>(null);

  // Anchor the add and edit/replace forms into view when they open.
  const addFormRef = useRef<HTMLDivElement>(null);
  const editFormRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editingId) {
      editFormRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [editingId]);

  useEffect(() => {
    if (isAdding) {
      addFormRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [isAdding]);

  const handleAddClick = useCallback(() => {
    setEditingId(null);
    setIsAdding(true);
  }, []);
  const handleFormClose = useCallback(() => setIsAdding(false), []);

  const handleEditClick = useCallback((id: string) => {
    setIsAdding(false);
    setEditingId(id);
  }, []);
  const handleEditClose = useCallback(() => setEditingId(null), []);

  const handleRemoveClick = useCallback((id: string) => setRemoveId(id), []);
  const handleRemoveClose = useCallback(() => setRemoveId(null), []);

  const interestsCountByType = useMemo(
    () => countInterestsByType(additionalInterests),
    [additionalInterests]
  );

  // Item pending removal — drives the confirmation modal.
  const interestToRemove = useMemo(
    () =>
      additionalInterests.find((ai) => ai.additionalInterestId === removeId) ??
      null,
    [additionalInterests, removeId]
  );

  const handleRemoveConfirm = useCallback(() => {
    setValue(
      "additionalInterests",
      getValues("additionalInterests").filter(
        (ai) => ai.additionalInterestId !== removeId
      ),
      { shouldDirty: true }
    );
    if (editingId === removeId) setEditingId(null);
    setRemoveId(null);
  }, [editingId, getValues, removeId, setValue]);

  const handleAddSubmit = useCallback(
    (data: AdditionalInterestFormData) => {
      const newInterest = {
        additionalInterestId: crypto.randomUUID(),
        isBilling: false,
        ...toInterestFields(data),
      } as InsuredTabAdditionalInterest;

      setValue(
        "additionalInterests",
        [...getValues("additionalInterests"), newInterest],
        { shouldDirty: true }
      );
      setIsAdding(false);
    },
    [getValues, setValue]
  );

  const handleEditSubmit = useCallback(
    (data: AdditionalInterestFormData) => {
      setValue(
        "additionalInterests",
        getValues("additionalInterests").map((ai) =>
          ai.additionalInterestId === editingId
            ? ({
                ...ai,
                ...toInterestFields(data),
              } as InsuredTabAdditionalInterest)
            : ai
        ),
        { shouldDirty: true }
      );
      setEditingId(null);
    },
    [editingId, getValues, setValue]
  );

  return {
    expandOnInit,
    sectionRef,
    additionalInterests,
    isTenantPolicy,
    isAdding,
    editingId,
    addFormRef,
    editFormRef,
    interestsCountByType,
    interestToRemove,
    handleAddClick,
    handleFormClose,
    handleAddSubmit,
    handleEditClick,
    handleEditClose,
    handleEditSubmit,
    handleRemoveClick,
    handleRemoveClose,
    handleRemoveConfirm,
  };
};
